import Tag from "@ingka/tag";
import InputField from "@ingka/input-field";
import { useEffect, useRef, useMemo } from "react";
import { usePrevious } from "@/hooks/misc";
import { validateOutput } from "./utils";
import LabelWithToolTip from "@/components/LabelWithTooltip/LabelWithToolTip";
import { useMetadataFields } from "@/components/MetadataFields/hooks";

type Props = {
  disabled: boolean;
};

const Tags = ({ disabled }: Props) => {
  const { activeAsset, updateMetadata, isMetadataUpdating } =
    useMetadataFields();
  const tags = useMemo(() => activeAsset.tags || [], [activeAsset.tags]);
  const previousTags = usePrevious(tags) || tags;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const updateTags = (tag: string) => {
    if (!tag) {
      return;
    }
    updateMetadata({
      tags: validateOutput(tags, tag),
    });
  };
  const isUpdating = isMetadataUpdating(activeAsset.id!, "tags");

  useEffect(() => {
    if (tags.length !== previousTags.length) {
      inputRef.current?.focus();
    }
  }, [tags, previousTags]);

  return (
    <>
      <LabelWithToolTip
        labelText="Tags"
        toolTipText={`Use free text tags to add information to the asset(s) that hasn't been covered by the previous fields. This could for example be a chance make the asset show up by searching for a specific word or phrase like 'Valentine' or 'compact living'.`}
      />
      <div className="mb-space150 flex flex-wrap gap-space75">
        {tags.length > 0 &&
          tags.map((tag) => (
            <Tag
              key={tag}
              label={tag}
              onClick={() => {
                if (disabled) return;
                updateMetadata({
                  tags: tags.filter((t) => t !== tag),
                });
              }}
            />
          ))}
      </div>
      <InputField
        ref={inputRef}
        id="tags"
        label="Additional free text tags"
        type="text"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          const input = e.target as HTMLInputElement;
          updateTags(input.value.trim());
          input.value = "";
        }}
        onBlur={(e) => {
          const input = e.target as HTMLInputElement;
          updateTags(input.value.trim());
          input.value = "";
        }}
        disabled={disabled || isUpdating}
      />
    </>
  );
};

export default Tags;
